import { useCallback, useState } from 'react';
import { useDebouncedValue } from './useDebouncedValue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pagesService } from '../services/pagesService';
import { notificationService } from '../services/notificationService';
import { invalidateAfterAlertChange, queryKeys } from '../api/queryClient';
import { useAuth } from './useAuth';
import { useDismissableError } from './useDismissableError';

export function useAdminAlerts() {
  const { applyViewer } = useAuth();
  const queryClient = useQueryClient();
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('all');
  const debouncedQ = useDebouncedValue(q, 300);

  const filters = { q: debouncedQ, kind };

  const query = useQuery({
    queryKey: queryKeys.adminAlerts(filters),
    queryFn: async () => {
      const data = await pagesService.adminAlerts({
        limit: 50,
        q: debouncedQ || undefined,
        kind: kind !== 'all' ? kind : undefined,
      });
      if (data?.viewer) applyViewer(data.viewer);
      return data;
    },
    staleTime: 8_000,
    refetchOnWindowFocus: true,
  });

  const createMutation = useMutation({
    mutationFn: (body) => notificationService.create(body),
    onSuccess: () => {
      void invalidateAfterAlertChange(queryClient);
    },
  });

  const load = useCallback(
    (nextQ = q, nextKind = kind) => {
      setQ(nextQ);
      setKind(nextKind);
      return queryClient.invalidateQueries({
        queryKey: ['pages', 'admin', 'alerts'],
      });
    },
    [q, kind, queryClient],
  );

  const { error, setError } = useDismissableError(query.error);

  return {
    items: query.data?.items ?? [],
    composeDefaults: query.data?.composeDefaults,
    loading: query.isLoading || (query.isFetching && !(query.data?.items?.length)),
    error,
    busy: createMutation.isPending,
    q,
    setQ,
    kind,
    setKind,
    setError,
    load,
    create: (body) => createMutation.mutateAsync(body),
  };
}
